import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { sendMessageToAgent } from "../../store/userSlice";
import { v4 as uuid } from "uuid";
import { Agents } from "../../types/master";
import { MessageInputHandle } from "../ChatWindow/MessageInput";

/**
 * ChatCore - Hook for unified chat logic
 * Returns all the logic and state needed for chat components
 * Each component can render its own UI with its own styles
 */
export const useChatCore = (
  sessionId: string,
  currentAgent?: Agents,
  agentId?: string
) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<MessageInputHandle | null>(null);

  // Redux state
  const { selectedAgent, currentChat, loading, isViewingHistory } = useSelector(
    (state: RootState) => state.user
  );
  const { loading: agentsLoading } = useSelector(
    (state: RootState) => state.agents
  );

  // Local state
  const [value, setValue] = useState("");
  const [userMessage, setUserMessage] = useState<string>("");

  // Derived state
  const agent = currentAgent || selectedAgent;

  // Filter out JSON payload messages from display (but keep them in storage)
  const visibleChat = useMemo(() => {
    return currentChat.filter(msg => {
      if (msg.sender !== "user") {
        return true;
      }
      const trimmed = msg.message.trim();
      if (trimmed.startsWith('{"action":')) {
        return false;
      }
      if (trimmed.includes("=== NDA_FEEDBACK_METADATA ===")) {
        return false;
      }
      return true;
    });
  }, [currentChat]);

  const hasMessages = visibleChat && visibleChat.length > 0;
  const showFullScreenSpinner = (loading && !hasMessages) || agentsLoading;

  // Get default prompts
  const defaultPrompts = useMemo(() => {
    if (!agent?.default_prompts) return [];
    try {
      return typeof agent.default_prompts === "string"
        ? JSON.parse(agent.default_prompts)
        : agent.default_prompts;
    } catch {
      return [];
    }
  }, [agent]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    const message = userMessage || value;
    if (!message.trim()) return;

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    try {
      dispatch(
        sendMessageToAgent({
          agentId: agent!.id,
          message,
          questionId,
          timestamp,
          agentEndpoint: agent?.endpoint,
          sessionId,
        })
      );
    } catch (error) {
      console.error("Error while sending agent query", error);
    } finally {
      setValue("");
      setUserMessage("");
    }
  }, [dispatch, agent, sessionId, userMessage, value]);

  // Handle prompt click
  const handlePromptClick = useCallback((prompt: string) => {
    inputRef.current?.sendPrompt(prompt);
  }, []);

  // Handle key down
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !loading) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [loading, handleSubmit]
  );

  return {
    // State
    agent,
    currentChat: visibleChat, // Return filtered chat without hidden messages
    loading,
    agentsLoading,
    hasMessages,
    showFullScreenSpinner,
    defaultPrompts,
    value,
    setValue,
    userMessage,
    setUserMessage,
    isViewingHistory,

    // Refs
    inputRef,

    // Handlers
    handleSubmit,
    handlePromptClick,
    handleKeyDown,
  };
};
